import React, {
  useContext, useEffect, useState, useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Layout, Table, notification, Row, Col, Card, Statistic, Select, Button, DatePicker,
} from 'antd';
import { API } from 'aws-amplify';
import moment from 'moment-timezone';
import { Decimal } from 'decimal.js';
import { unparse } from 'papaparse';
import PageHeaderBar from '../../components/PageHeaderBar';
import Context from '../../context';
import CapacitySubscriptionChargeChart from './CapacitySubscriptionChargeChart';
import './index.less';
import { calculateWithUnit } from '../../utils/balanceGroupStatsHelpers';
import { formatNumber } from '../../utils/i18n';
import { UNITS_EXTENDED } from '../../constants/units';
import UTF8_BOM from '../../constants/excel';
import { startOfGasDay, endOfGasDay } from '../../utils/gasday';

const { MonthPicker } = DatePicker;

const CapacitySubscriptionCharge = () => {
  const { t } = useTranslation();
  const { currentUser, selectedMarketPartyId } = useContext(Context);
  const { inAdminGroup, inReadOnlyAdminGroup } = currentUser || {};
  // eslint-disable-next-line no-unused-vars
  const isAdmin = inAdminGroup || inReadOnlyAdminGroup;

  const [allYearData, setAllYearData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counterpartyMetadata, setCounterpartyMetadata] = useState({});
  const [annualPeak, setAnnualPeak] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(UNITS_EXTENDED.MWH);
  const unitLabel = selectedUnit === UNITS_EXTENDED.MWH ? t('common.label.mwhPerHour') : t('common.label.kwhPerHour');

  const [yearlyPeakInfo, setYearlyPeakInfo] = useState({
    value: 0, timestamp: null, subscription: 0, year: null,
  });
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [hasAutoSelectedMonth, setHasAutoSelectedMonth] = useState(false);
  const [isPreparingCsvData, setIsPreparingCsvData] = useState(false);

  const renderNumberOrDash = (val, unitOverride) => {
    const unit = unitOverride || selectedUnit;
    if (val === null || val === undefined || val === '') return '-';
    const converted = calculateWithUnit(unit, val);
    const num = converted ? converted.toNumber() : NaN;
    return (Number.isNaN(num)) ? '-' : formatNumber(num, 3);
  };

  const fetchData = async (marketPartyId, year, isInitialFetch = false) => {
    if (!marketPartyId || marketPartyId === 'admin') return;

    setLoading(true);
    try {
      const response = await API.get('FINTSO', `/marketparties/${marketPartyId}/capacity-subscription-charge/summary`, {
        queryStringParameters: {
          year,
          resolution: 'hour',
        },
      });

      setCounterpartyMetadata(response.counterpartyMetadata || {});
      setAnnualPeak(response.annualPeak || null);

      const chartData = (response.values || [])
        .map((row) => {
          const timestamp = new Decimal(row.timestamp || 0).mul(1000).toNumber();
          const gasDay = row.gasDay || '';
          const allocatedEnergy = new Decimal(row.allocatedEnergy || 0).toNumber();
          const capacitySubscription = new Decimal(
            row.capacitySubscription || row.subscribedCapacity || 0,
          ).toNumber();
          const counterpartyBreakdown = row.counterpartyBreakdown || {};

          return {
            timestamp,
            gasDay,
            allocatedEnergy,
            capacitySubscription,
            counterpartyBreakdown,
          };
        })
        .sort((a, b) => a.timestamp - b.timestamp);

      setAllYearData(chartData);

      if (response.annualPeak) {
        const { annualPeak: peak } = response;
        const peakTimestampRaw = peak.timestamp || peak.peakTimestamp;
        const peakTimestamp = peakTimestampRaw
          ? new Decimal(peakTimestampRaw).mul(1000).toNumber()
          : null;
        const peakValue = new Decimal(peak.peakValue || peak.peakEnergy || 0).toNumber();

        setYearlyPeakInfo({
          value: peakValue,
          timestamp: peakTimestamp,
          subscription: new Decimal(
            peak.capacitySubscriptionAtPeak || peak.subscribedCapacity || 0,
          ).toNumber(),
          year: peak.year,
        });

        // Auto-select the month to focus on when the page first loads
        if (isInitialFetch && !hasAutoSelectedMonth && peakTimestamp) {
          // Case A: Peak found.  Let's set the chart to that specific month.
          const peakMoment = moment(peakTimestamp);
          setSelectedMonth(peakMoment.clone().startOf('month'));
          setHasAutoSelectedMonth(true);
        } else if (isInitialFetch && !hasAutoSelectedMonth) {
          // Case B: The API responded but didn't find a peak (maybe a new year with no data).
          // Fallback to the current month so the user doesn't see a blank screen.
          setSelectedMonth(moment().startOf('month'));
          setHasAutoSelectedMonth(true);
        }
      } else if (isInitialFetch && !hasAutoSelectedMonth) {
        // Case C: The API response was successful but completely empty.
        // Again, fallback to the current month to keep the UI active.
        setSelectedMonth(moment().startOf('month'));
        setHasAutoSelectedMonth(true);
      }
    } catch (error) {
      notification.error({
        message: t('common.notifications.errorFetching'),
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setHasAutoSelectedMonth(false);
    fetchData(selectedMarketPartyId, selectedYear, true);
  }, [selectedMarketPartyId, selectedYear]);

  const monthData = useMemo(() => {
    if (!selectedMonth) return [];
    // We need to calculate this here instead of backend, because the timerange selection (month)
    // happens in frontend
    // Start of the gas month: 07:00 on the 1st day of the selected month
    const start = startOfGasDay(selectedMonth.clone().startOf('month')).valueOf();
    // End of the gas month: 07:00 on the 1st day of the NEXT month
    const end = endOfGasDay(selectedMonth.clone().endOf('month')).valueOf();
    const filtered = allYearData.filter(d => d.timestamp >= start && d.timestamp < end);

    const rangePeakValue = filtered.reduce(
      (max, d) => (new Decimal(d.allocatedEnergy).gt(max) ? d.allocatedEnergy : max),
      0,
    );

    return filtered.map(d => ({ ...d, tehohuippu: rangePeakValue }));
  }, [allYearData, selectedMonth]);

  const handleExportToExcel = async () => {
    if (allYearData.length === 0) return;
    setIsPreparingCsvData(true);

    try {
      const headers = [
        'Gas Day',
        `${t('capacitySubscriptionCharge.table.timestampTable')} (UTC)`,
        `${t('capacitySubscriptionCharge.table.allocatedEnergyTable')} (${unitLabel})`,
        `${t('capacitySubscriptionCharge.table.capacitySubscription')} (${unitLabel})`,
        ...Object.values(counterpartyMetadata).map(c => `${c.name} (${unitLabel})`),
      ];

      const csvRows = allYearData.map((d) => {
        const rowData = [
          d.gasDay,
          moment.utc(d.timestamp).format('DD.MM.YYYY HH:mm'),
          renderNumberOrDash(d.allocatedEnergy),
          renderNumberOrDash(d.capacitySubscription),
        ];
        Object.keys(counterpartyMetadata).forEach((code) => {
          rowData.push(renderNumberOrDash(d.counterpartyBreakdown[code]));
        });
        return rowData;
      });

      const csv = unparse({ fields: headers, data: csvRows });
      const blob = new Blob([UTF8_BOM + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CapacitySubscriptionCharge_${selectedYear}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      notification.error({ message: 'Export failed', description: e.message });
    } finally {
      setIsPreparingCsvData(false);
    }
  };

  const TZ = 'Europe/Helsinki';

  const toHelsinki = (ts) => moment(ts).tz(TZ);

  const formattedTimestamp = useMemo(() => {
    if (!yearlyPeakInfo.timestamp) return '-';
    const m = toHelsinki(yearlyPeakInfo.timestamp);
    return m.isValid() ? m.format('DD.MM.YYYY HH:mm') : '-';
  }, [yearlyPeakInfo.timestamp]);

  const renderCounterpartyPeaksTable = () => {
    if (!annualPeak) return null;

    const rawPeaks = annualPeak.counterpartyPeaks || annualPeak.counterPartyPeaks || [];
    const rawArray = Array.isArray(rawPeaks)
      ? rawPeaks
      : Object.entries(rawPeaks).map(([id, val]) => ({
        code: id,
        peakEnergy: val.peakValue || val.peakEnergy || val,
        peakTimestamp: val.timestamp || val.peakTimestamp,
      }));

    // Normalise to consistent field names regardless of API shape
    const peakEntries = rawArray.map(entry => ({
      counterpartyId: entry.counterpartyId || entry.code || entry.id,
      peakValue: entry.peakValue != null ? entry.peakValue : entry.peakEnergy,
      timestamp: entry.timestamp != null ? entry.timestamp : entry.peakTimestamp,
    }));

    if (peakEntries.length === 0) return null;

    const cpColumns = [
      {
        title: t('capacitySubscriptionCharge.counterpartyPeaks.name'),
        dataIndex: 'counterpartyId',
        key: 'counterpartyId',
        render: (id) => {
          const meta = counterpartyMetadata[id];
          if (!meta) return id;
          return meta.eic ? `${meta.name} (${meta.eic})` : meta.name;
        },
      },
      {
        title: `${t('capacitySubscriptionCharge.counterpartyPeaks.peak')} (${unitLabel})`,
        dataIndex: 'peakValue',
        key: 'peakValue',
        align: 'right',
        render: (val) => renderNumberOrDash(val),
      },
      {
        title: t('capacitySubscriptionCharge.counterpartyPeaks.timestamp'),
        dataIndex: 'timestamp',
        key: 'timestamp',
        align: 'right',
        render: (ts) => (ts
          ? toHelsinki(new Decimal(ts).mul(1000).toNumber()).format('DD.MM.YYYY HH:mm')
          : '-'),
      },
    ];

    return (
      <Row type="flex" justify="start" style={{ marginTop: '20px', marginBottom: '24px' }}>
        <Col xs={24} lg={18} xl={16}>
          <Card title={`${t('capacitySubscriptionCharge.counterpartyPeaks.title')} (${selectedYear})`}>
            <Table
              dataSource={peakEntries}
              columns={cpColumns}
              pagination={false}
              size="small"
              rowKey="counterpartyId"
            />
          </Card>
        </Col>
      </Row>
    );
  };

  return (
    <div className="capacity-subscription-charge">
      <PageHeaderBar
        title={t('capacitySubscriptionCharge.title')}
        span={{ title: 12, action: 12 }}
        actionItems={<></>}
      />
      <Layout.Content className="layout__container__page__content">
        <Card style={{ marginBottom: '24px' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={t('capacitySubscriptionCharge.gasYearPeak')}
                value={yearlyPeakInfo.year ? yearlyPeakInfo.year : '-'}
                formatter={(val) => val}
                valueStyle={{ fontSize: '16px', fontWeight: 'bold' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={t('capacitySubscriptionCharge.table.charge')}
                value={renderNumberOrDash(yearlyPeakInfo.value, UNITS_EXTENDED.MWH)}
                suffix={` ${t('common.label.mwhPerHour')}`}
                valueStyle={{ fontSize: '16px', fontWeight: 'bold' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={t('capacitySubscriptionCharge.table.timestamp')}
                value={formattedTimestamp}
                valueStyle={{ fontSize: '16px', fontWeight: 'bold' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={t('capacitySubscriptionCharge.table.capacitySubscription')}
                value={renderNumberOrDash(yearlyPeakInfo.subscription, UNITS_EXTENDED.MWH)}
                suffix={` ${t('common.label.mwhPerHour')}`}
                valueStyle={{ fontSize: '16px', fontWeight: 'bold' }}
              />
            </Col>
          </Row>
        </Card>

        <div style={{
          marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between',
        }}
        >
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <MonthPicker
              value={selectedMonth}
              onChange={(val) => {
                if (val) {
                  setSelectedMonth(val.clone().startOf('month'));
                  setSelectedYear(val.year());
                }
              }}
              allowClear={false}
              placeholder=""
            />
            <div className="unit-selector" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div className="title" style={{ color: 'rgba(0,0,0,0.45)' }}>
                {t('common.label.unit')}
              </div>
              <Select
                value={selectedUnit}
                onChange={setSelectedUnit}
                style={{ width: 120 }}
              >
                <Select.Option value={UNITS_EXTENDED.KWH}>
                  {t('common.label.kwhPerHour')}
                </Select.Option>
                <Select.Option value={UNITS_EXTENDED.MWH}>
                  {t('common.label.mwhPerHour')}
                </Select.Option>
              </Select>
            </div>
          </div>
          <Button
            icon="download"
            onClick={handleExportToExcel}
            disabled={allYearData.length === 0}
            loading={isPreparingCsvData}
            type="primary"
          >
            {`Export ${selectedYear} data to CSV`}
          </Button>
        </div>

        <div style={{ marginBottom: '24px', background: '#fff', padding: '24px' }}>
          <CapacitySubscriptionChargeChart
            data={monthData}
            selectedUnit={selectedUnit}
            loading={loading}
          />
        </div>

        {renderCounterpartyPeaksTable()}

        <Row type="flex" justify="start">
          <Col xs={24} lg={18} xl={16}>
            <Card
              title={`${t('capacitySubscriptionCharge.table.allocatedEnergyTable')} (${selectedMonth ? selectedMonth.format('MMMM YYYY') : '-'})`}
              style={{ marginBottom: '24px' }}
            >
              <Table
                dataSource={monthData}
                loading={loading}
                rowKey="timestamp"
                size="small"
                pagination={{
                  pageSize: 168,
                  size: 'small',
                  position: 'bottom',
                  showSizeChanger: false,
                }}
                columns={[
                  {
                    title: t('capacitySubscriptionCharge.table.timestampTable'),
                    dataIndex: 'timestamp',
                    key: 'timestamp',
                    render: (ts) => toHelsinki(ts).format('DD.MM.YYYY HH:mm'),
                  },
                  {
                    title: `${t('capacitySubscriptionCharge.table.allocatedEnergyTable')} (${unitLabel})`,
                    dataIndex: 'allocatedEnergy',
                    key: 'allocatedEnergy',
                    align: 'right',
                    render: (val) => renderNumberOrDash(val),
                  },
                  {
                    title: `${t('capacitySubscriptionCharge.table.capacitySubscription')} (${unitLabel})`,
                    dataIndex: 'capacitySubscription',
                    key: 'capacitySubscription',
                    align: 'right',
                    render: (val) => renderNumberOrDash(val),
                  },
                  {
                    title: `${t('capacitySubscriptionCharge.table.charge')} (${unitLabel})`,
                    dataIndex: 'tehohuippu',
                    key: 'tehohuippu',
                    align: 'right',
                    render: (val) => renderNumberOrDash(val),
                  },
                ]}
              />
            </Card>
          </Col>
        </Row>
      </Layout.Content>
    </div>
  );
};

export default CapacitySubscriptionCharge;
